import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  CMS_FORM_SELECT_TRIGGER_CLASS,
  CMS_GOOGLE_FONTS,
  getGoogleFontDefinitionByFamily,
  normalizeCmsTypography,
  resolveCmsFontVariant,
  resolveCmsFontWeight,
  type CmsTypographySelection,
} from "@/lib/cms/cms-theme.constants";
import { CmsAppearanceHint } from "@/components/cms/theme/CmsAppearanceHint";
import { cn } from "@/lib/utils";
import { Check, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

type Props = {
  label: string;
  value: CmsTypographySelection;
  onChange: (value: CmsTypographySelection) => void;
  description?: string;
  allowNone?: boolean;
  /**
   * Optional typography to DISPLAY when `value` is inherited (null/null),
   * without changing the stored value.
   */
  inheritDisplayValue?: CmsTypographySelection | null;
  noneLabel?: string;
  previewText?: string;
  /** Masque le bloc d'aperçu sous le sélecteur. */
  hidePreview?: boolean;
  className?: string;
};

export function CmsTypographyPicker({
  label,
  value,
  onChange,
  description,
  allowNone = false,
  inheritDisplayValue = null,
  noneLabel,
  previewText,
  hidePreview = false,
  className,
}: Props) {
  const { t } = useTranslation();

  const isInheritedValue = value.fontFamily == null && value.fontVariant == null;

  const displayFontFamily = value.fontFamily ?? inheritDisplayValue?.fontFamily ?? null;
  const displayFontVariant = value.fontVariant ?? inheritDisplayValue?.fontVariant ?? null;

  const normalizedValue = normalizeCmsTypography(displayFontFamily, displayFontVariant);
  const currentFamily = normalizedValue.fontFamily;
  const currentVariant = normalizedValue.fontVariant;
  const currentFont = getGoogleFontDefinitionByFamily(currentFamily);
  const previewFamily = currentFamily ?? "Instrument Sans";
  const previewVariant = resolveCmsFontVariant(previewFamily, currentVariant);
  const resolvedNoneLabel =
    noneLabel ?? t("cms.typography.none");
  const resolvedPreviewText =
    previewText ?? t("cms.typography.previewText");
  const currentSelectionLabel =
    currentFont != null
      ? `${currentFont.label} — ${previewVariant.label}`
      : resolvedNoneLabel;

  return (
    <div className={cn("space-y-3", className)}>
      <div className="max-w-prose space-y-1">
        <label className="text-sm font-medium text-slate-700">{label}</label>
        {description ? (
          <CmsAppearanceHint size="sm">{description}</CmsAppearanceHint>
        ) : null}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={cn(
              "h-auto w-full justify-between gap-3 px-3 py-2.5 text-left",
              CMS_FORM_SELECT_TRIGGER_CLASS,
            )}
          >
            <span
              className="min-w-0 max-w-prose truncate text-sm text-slate-800"
              style={{
                fontFamily: `'${previewFamily}', sans-serif`,
                fontWeight: resolveCmsFontWeight(previewFamily, previewVariant.id),
              }}
            >
              {currentSelectionLabel}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[280px] p-1.5">
          <div className="max-h-[320px] overflow-y-auto pr-1">
            {allowNone ? (
              <DropdownMenuItem
                onClick={() =>
                  onChange({ fontFamily: null, fontVariant: null })
                }
                className={cn(
                  "cursor-pointer gap-2 rounded-md px-2.5 py-2 data-[highlighted]:text-white",
                  isInheritedValue && "bg-accent text-accent-foreground",
                )}
              >
                <Check
                  className={cn(
                    "h-4 w-4 shrink-0",
                    isInheritedValue ? "opacity-100" : "opacity-0",
                  )}
                />
                <div className="min-w-0 truncate text-sm font-medium">
                  {resolvedNoneLabel}
                </div>
              </DropdownMenuItem>
            ) : null}

            {CMS_GOOGLE_FONTS.map((font) => {
              const isCurrentFont = currentFamily === font.googleFontFamily;

              return (
                <DropdownMenuSub key={font.id}>
                  <DropdownMenuSubTrigger
                    className={cn(
                      "cursor-pointer rounded-md px-2.5 py-2 data-[highlighted]:text-white data-[state=open]:text-white",
                      isCurrentFont && "bg-accent text-white",
                    )}
                  >
                    <div
                      className="min-w-0 truncate text-sm font-medium"
                      style={{
                        fontFamily: `'${font.googleFontFamily}', sans-serif`,
                      }}
                    >
                      {font.label}
                    </div>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-[250px] p-1.5">
                    <div className="max-h-[280px] overflow-y-auto pr-1">
                      {font.variants.map((variant) => {
                        const isSelected =
                          currentFamily === font.googleFontFamily &&
                          previewVariant.id === variant.id;

                        return (
                          <DropdownMenuItem
                            key={`${font.id}-${variant.id}`}
                            onClick={() =>
                              onChange(
                                normalizeCmsTypography(
                                  font.googleFontFamily,
                                  variant.id,
                                ),
                              )
                            }
                            className={cn(
                              "cursor-pointer gap-2 rounded-md px-2.5 py-2 data-[highlighted]:text-white",
                              isSelected && "bg-accent text-white",
                            )}
                          >
                            <Check
                              className={cn(
                                "h-4 w-4 shrink-0",
                                isSelected ? "opacity-100" : "opacity-0",
                              )}
                            />
                            <div className="min-w-0">
                              <div
                                className="truncate text-sm font-medium"
                                style={{
                                  fontFamily: `'${font.googleFontFamily}', sans-serif`,
                                  fontWeight: variant.weight,
                                }}
                              >
                                {variant.label}
                              </div>
                            </div>
                          </DropdownMenuItem>
                        );
                      })}
                    </div>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              );
            })}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {!hidePreview ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2">
          <p className="text-xs uppercase tracking-wide text-slate-500">
            {t("cms.typography.preview")}
          </p>
          <p
            // Aperçu rendu à 16px (= taille du corps de texte CMS, cf. canvas/front)
            // pour qu'il reflète fidèlement le rendu réel du bloc, et non un 14px UI.
            className="mt-2 text-base text-slate-800"
            style={{
              fontFamily: `'${previewFamily}', sans-serif`,
              fontWeight: resolveCmsFontWeight(previewFamily, previewVariant.id),
            }}
          >
            {resolvedPreviewText}
          </p>
        </div>
      ) : null}
    </div>
  );
}
